import { usePageState } from "@/navigation/NavigationProvider";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ExternalLink, GripVertical, Lock, RotateCcw, Pencil, Unlock } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BoardLoading from "@/components/BoardLoading";
import BoardWorkspace from "@/components/BoardWorkspace";
import CompactBoardToolbar from "@/components/CompactBoardToolbar";
import BoardDividerRow from "@/components/BoardDividerRow";
import { BoardNotesPreview } from "@/components/BoardNotesPreview";
import BoardHighlightPopover from "@/components/BoardHighlightPopover";
import {
  InformationalStatusText,
} from "@/components/InformationalStatusText";
import { locateCellView } from "@/utils/locateCell";
import ProjectQuickStatusEditor from "@/components/ProjectQuickStatusEditor";
import MasterBoardQuickEditor from "@/components/MasterBoardQuickEditor";
import { PermitReadinessLabel } from "@/components/PermitCalculatedBadge";
import { ManagerWorkflowDialog } from "@/components/ManagerWorkflowDialog";
import { useAuth } from "@/auth/AuthContext";
import { getProjectModuleAvailability } from "@/utils/projectFeatures";
import { useBoardOrganization, HIGHLIGHT_CELLS } from "@/hooks/useBoardOrganization";
import { useManagerOrder } from "@/hooks/useManagerOrder";
import { useBoardDrag } from "@/hooks/useBoardDrag";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useSpreadsheetGrid } from "@/hooks/useSpreadsheetGrid";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import BoardColumnsDialog from "@/components/BoardColumnsDialog";
import { formatBoardDate } from "@/utils/boardDate";
import { useTimeRefresh } from "@/hooks/useTimeRefresh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const MANAGER_PROJECT_LINK_STATE = { from: "manager" };

const columns = [
  ["project_number", "Project", true],
  ["due_date", "Due"],
  ["operational_status", "Stage"],
  ["customer_supervisor", "Cust supervisor"],
  ["permit_status", "Permit"],
  ["locate_status", "Locate"],
  ["locate_ticket_number", "Ticket"],
  ["water_locate", "Water"],
  ["next_scheduled_date", "Sched"],
  ["recent_notes", "Notes"],
];

function operationalStatusLabel(row) {
  if (row.operational_status === "not_started") return "Not Started";
  return (row.operational_status || "—").replaceAll("_", " ");
}

export default function ManagerBoard() {
  const { canAccessBilling, canCreateFieldNotes, canManageRecords, canManageProjectWorkflow, hasPermission, user } = useAuth();
  const features = user?.features || {};
  const modules = getProjectModuleAvailability(features);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = usePageState("query", "");
  const [status, setStatus] = usePageState("status", "all");
  const [viewMode, setViewMode] = usePageState("viewMode", () => window.localStorage.getItem("manager-board-view") || "all");
  const columnResize = useColumnResize({ boardKey: "manager", userId: user?.id });
  const [editingProject, setEditingProject] = useState(null);
  const [quickProject, setQuickProject] = useState(null);
  const grid = useSpreadsheetGrid({
    onEnter: (rowKey) => {
      const project = (data?.rows || []).find((r) => r.id === rowKey);
      if (project) setQuickProject(project);
    },
  });
  const [stagePickerProject, setStagePickerProject] = useState(null);
  const [linkedRecordEditor, setLinkedRecordEditor] = useState(null);
  const [folderType, setFolderType] = useState("");
  const [folderId, setFolderId] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [highlightPopover, setHighlightPopover] = useState(null);
  const [addDividerOpen, setAddDividerOpen] = useState(false);
  const [dividerDraft, setDividerDraft] = useState({ label: "", section_key: "in_progress" });
  const currentFolder = data?.current_folder;
  const isProjectManager = user?.role === "project_manager";
  const managerId = currentFolder?.type === "manager" ? currentFolder.id : "";
  const canOrganize = !!managerId && ["owner_admin", "office_admin", "project_manager"].includes(user?.role);
  const navigate = useNavigate();

  const loadBoard = useCallback(async () => {
    if (user?.role === "supervisor") return;
    try {
      setError("");
      setData(
        (await axios.get(`${API}/manager-project-board`, {
          params: folderType ? { folder_type: folderType, folder_id: folderId } : {},
        })).data,
      );
    } catch {
      setError("The Manager Board could not be loaded. Please try again.");
    }
  }, [folderId, folderType, user?.role]);

  const canManageShared = !!hasPermission?.("board:organize");
  const sharedOrg = useBoardOrganization(canManageShared, loadBoard);
  const managerOrder = useManagerOrder(managerId, loadBoard);
  const boardOrg = { ...sharedOrg, ...managerOrder };
  useTimeRefresh(loadBoard, data);

  const readRowFromDom = useCallback((element) => {
    if (!element || !element.getAttribute) return null;
    const id = element.getAttribute("data-board-row-id");
    if (!id) return null;
    return {
      id,
      kind: element.getAttribute("data-board-row-kind") || "project",
      sectionKey: element.getAttribute("data-board-section-key") || "",
      version: Number(element.getAttribute("data-bo-version") || 0),
    };
  }, []);
  const drag = useBoardDrag({
    readRowFromDom,
    onDrop: async (moving, sectionKey, after) => {
      if ((boardOrg.frozenSections || []).includes(sectionKey)) {
        toast.error("This section is frozen — unfreeze it to reorder.");
        return;
      }
      const allRows = (data?.sections || []).flatMap((s) => s.rows || []);
      const rowRef = allRows.find((r) => r.type === moving.kind && r.id === moving.id);
      const version = boardOrg.sectionOrders[sectionKey]?.version ?? 0;
      if (moving.kind === "project") {
        await boardOrg.move(moving.id, sectionKey, after, version);
      } else {
        await boardOrg.moveDivider(moving.id, sectionKey, after, version);
      }
    },
  });

  useEffect(() => { loadBoard(); }, [loadBoard, modules.permits, modules.locates]);
  useEffect(() => { window.localStorage.setItem("manager-board-view", viewMode); }, [viewMode]);

  const fieldPlanStates = useMemo(() => new Set(["in_progress"]), []);

  const managerSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const source = data?.sections?.length ? data.sections : [
      { key: "in_progress_projects", label: "In Progress Projects",
        description: "All projects currently in the in-progress lifecycle state.",
        rows: data?.rows || [] },
    ];
    return source.map((section) => ({
      ...section,
      items: section.rows.filter((row) => {
        if (row.type === "divider") return true;
        if (viewMode === "field_plan" && !fieldPlanStates.has(row.operational_status)) return false;
        if (status !== "all" && row.operational_status !== status) return false;
        if (q) {
          const hay = [
            row.project_number, row.project_name, row.customer, row.customer_supervisor,
            row.project_manager_names?.join(" "), row.remaining_work, row.what_is_invoiced,
          ].join(" ").toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      }),
    }));
  }, [data, query, status, viewMode, fieldPlanStates]);

  const visibleProjectCount = managerSections.reduce(
    (acc, s) => acc + s.items.filter((i) => i.type !== "divider").length, 0);

  const selectFolder = (type, id) => {
    setEditingProject(null);
    setStatus("all");
    setViewMode("all");
    setFolderType(type);
    setFolderId(id);
  };

  const canHideColumns = hasPermission ? hasPermission("board:organize") : canOrganize;
  const colVis = useColumnVisibility({ board: "manager", columns, canHide: canHideColumns, userId: user?.id, features });
  const totalCols = colVis.visibleColumns.length + 1 + (canOrganize ? 1 : 0);
  const visibleColumns = colVis.visibleColumns;

  const openHighlight = (event, projectId, cellKey, current, sectionKey) => {
    event.preventDefault();
    if (!canManageShared || !HIGHLIGHT_CELLS.has(cellKey)) return;
    setHighlightPopover({ projectId, cellKey, current, position: { top: event.clientY, left: event.clientX }, sectionKey });
  };
  const applyHighlight = async (color) => {
    if (!highlightPopover) return;
    await boardOrg.setHighlight(highlightPopover.projectId, highlightPopover.cellKey, color, highlightPopover.sectionKey);
  };

  const submitDivider = async () => {
    const label = dividerDraft.label.trim();
    if (!label) return;
    await boardOrg.createDivider(dividerDraft.section_key, label, null, null);
    setAddDividerOpen(false);
    setDividerDraft({ label: "", section_key: dividerDraft.section_key });
  };

  if (user?.role === "supervisor") return <Navigate replace to="/master-project-board" />;
  if (!data && !error) return <BoardLoading label="Loading Manager Board" />;

  const summary = data?.summary || {};
  const folderLabel = currentFolder?.label || "Manager Board";
  const countChips = [
    { key: "active", value: summary.active || 0, label: "In progress", active: false, onClick: () => { setStatus("all"); setViewMode("all"); } },
  ];
  const folderChips = (data?.folders?.managers || []).map((f) => ({
    key: `folder-${f.id}`,
    value: f.count,
    label: f.label,
    active: currentFolder?.id === f.id,
    onClick: () => selectFolder(f.type, f.id),
  }));

  const overflowItems = [
    ...(canManageShared ? [{ key: "add-divider", label: "Add shared divider", testId: "manager-board-add-divider", onClick: () => setAddDividerOpen(true) }] : []),
    ...(canHideColumns ? [{ key: "columns", label: `Columns${colVis.hiddenCount ? ` (${colVis.hiddenCount} hidden)` : ""}`, testId: "manager-board-columns-toggle", onClick: () => colVis.setOpen(true) }] : []),
    { key: "master", label: "Open Master Board", testId: "board-switch-to-master", to: "/master-project-board", onNavigate: () => navigate("/master-project-board") },
  ];

  return (
    <div className="compact-board">
      <BoardWorkspace
        eyebrow=""
        headingTestId="manager-board-heading"
        title={folderLabel}
        subtitle=""
        actions={
          <CompactBoardToolbar
            countChips={[...countChips, ...folderChips]}
            filtersActive={viewMode === "field_plan"}
            onOpenFilters={() => setFiltersOpen((v) => !v)}
            onRefresh={loadBoard}
            onSearchChange={setQuery}
            overflowItems={overflowItems}
            searchValue={query}
            testIdPrefix="manager-board"
          />
        }
      >
        {error ? (
          <section className="board-error-state" data-testid="manager-board-error">
            <p>{error}</p>
            <button data-testid="manager-board-retry-button" onClick={loadBoard} type="button">Try again</button>
          </section>
        ) : (
          <section className="board-table-panel manager-board-table-panel" data-testid="manager-board-table-panel">
            {filtersOpen && (
              <div className="compact-board-toolbar" data-testid="manager-board-filters" style={{ borderTop: "1px solid #e2e8f0" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: "#64748b" }}>View</span>
                  <select data-testid="manager-view-select" onChange={(e) => { setStatus("all"); setViewMode(e.target.value); }} value={viewMode}>
                    <option value="all">All folder work</option>
                    <option value="field_plan">Today’s field plan</option>
                  </select>
                </label>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748b" }} data-testid="manager-board-results-count">
                  {visibleProjectCount} shown
                </span>
              </div>
            )}
            <div className="board-table-scroll">
              <table className="operations-data-table manager-data-table spreadsheet-grid" data-testid="manager-board-table" {...grid.tableProps}>
                <thead>
                  <tr>
                    {canOrganize && <th className="board-drag-cell" aria-label="Move" />}
                    {visibleColumns.map(([key, label, sticky]) => (
                      <th className={sticky ? "table-project-column" : ""} key={key} scope="col" style={columnResize.headerStyle(key)}>
                        <span data-mobile-label={label}>{label}</span>
                        <button
                          aria-label={`Resize ${label} column`}
                          className="col-resize-handle"
                          data-testid={`manager-col-resize-${key}`}
                          onDoubleClick={() => columnResize.resetColumn(key)}
                          onPointerDown={(event) => columnResize.beginDrag(event, key)}
                          tabIndex={-1}
                          title="Drag to resize · double-click to reset"
                          type="button"
                        />
                      </th>
                    ))}
                    <th scope="col"><span data-mobile-label="Workflow">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {managerSections.map((section) => {
                    const secKey = section.items.find((i) => i.type !== "divider")?.operational_status
                      || section.items.find((i) => i.type === "divider")?.section_key
                      || section.key;
                    const frozen = (boardOrg.frozenSections || []).includes(secKey);
                    return (
                    <Fragment key={section.key}>
                      <tr className="manager-readiness-section-row" data-testid={`manager-board-section-${section.key}`}>
                        <td colSpan={totalCols}>
                          <strong>{section.label}</strong>
                          <span>{section.items.filter((i) => i.type !== "divider").length} projects</span>
                          {frozen && (
                            <span className="board-section-frozen-badge" data-testid={`manager-section-frozen-${secKey}`} title="Section frozen — reordering disabled" style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3, color: "#b45309", fontWeight: 600 }}>
                              <Lock size={11} /> Frozen
                            </span>
                          )}
                          {canOrganize && (
                            <>
                            <button
                              className="board-section-freeze-btn"
                              data-testid={`manager-section-freeze-toggle-${secKey}`}
                              onClick={() => (frozen ? boardOrg.unfreezeSection(secKey) : boardOrg.freezeSection(secKey))}
                              title={frozen ? "Unfreeze section" : "Freeze section (lock ordering)"}
                              type="button"
                              style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, background: "transparent", border: "1px solid #cbd5e1", borderRadius: 4, padding: "1px 6px", cursor: "pointer" }}
                            >
                              {frozen ? <><Unlock size={12} /> Unfreeze</> : <><Lock size={12} /> Freeze</>}
                            </button>
                          <button
                            className="board-section-restore-btn"
                            data-testid={`manager-section-restore-${secKey}`}
                            disabled={frozen}
                            onClick={() => {
                              if (window.confirm("Restore this manager section to the current Master Board order?")) {
                                boardOrg.restoreSection(secKey);
                              }
                            }}
                            title={frozen ? "Unfreeze section to restore ordering" : "Follow Master Board ordering"}
                            type="button"
                            style={{ marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, border: "1px solid #cbd5e1", borderRadius: 4, padding: "1px 6px" }}
                          >
                            <RotateCcw size={12} /> Follow Master Order
                          </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {section.items.map((item) => item.type === "divider" ? (
                        <BoardDividerRow
                          canOrganize={canManageShared && !frozen}
                          canMove={canOrganize && !frozen}
                          colSpan={totalCols}
                          divider={item}
                          gripProps={canOrganize && !frozen ? drag.gripProps({ kind: "divider", id: item.id, sectionKey: item.section_key || "in_progress", version: item._bo_version }) : null}
                          key={item.id}
                          onDelete={boardOrg.deleteDivider}
                          onRename={(id, label, color, ver) => boardOrg.renameDivider(id, label, color === undefined ? item.color : color, ver)}
                          sectionKey={item.section_key || "in_progress"}
                        />
                      ) : (
                        <ManagerRow
                          canAccessBilling={canAccessBilling}
                          canCreateFieldNotes={canCreateFieldNotes}
                          canManageProjectWorkflow={canManageProjectWorkflow}
                          canManageRecords={canManageRecords}
                          canOrganize={canOrganize}
                          canQuickEdit={canAccessBilling}
                          gridCellProps={grid.cellProps}
                          gripProps={canOrganize && !frozen ? drag.gripProps({ kind: "project", id: item.id, sectionKey: item.operational_status || "in_progress", version: item._bo_version }) : null}
                          hiddenColumns={colVis.hidden}
                          key={item.id}
                          onEdit={setEditingProject}
                          onHighlightRequest={openHighlight}
                          onLinkedEdit={setLinkedRecordEditor}
                          onQuickEdit={setQuickProject}
                                                    onStagePick={() => setStagePickerProject(item)}
                          row={item}
                          rowProps={canOrganize && !frozen ? drag.rowProps({ kind: "project", id: item.id, sectionKey: item.operational_status || "in_progress", version: item._bo_version }) : null}
                        />
                      ))}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {visibleProjectCount === 0 && (
                <div className="board-empty-state" data-testid="manager-board-empty-state">
                  No field-actionable projects match these filters.
                </div>
              )}
            </div>
          </section>
        )}

        <ManagerWorkflowDialog
          canAccessBilling={canAccessBilling}
          companyLabels={data?.company_context?.labels || { projectManager: "Project Manager", supervisor: "Supervisor" }}
          onOpenChange={() => setEditingProject(null)}
          onSaved={loadBoard}
          project={editingProject}
        />
        {quickProject && (
          <ProjectQuickStatusEditor
            canAccessBilling={canAccessBilling}
            onClose={() => setQuickProject(null)}
            onSaved={loadBoard}
            project={quickProject}
          />
        )}
        {stagePickerProject && (
          <ProjectQuickStatusEditor
            canAccessBilling={canAccessBilling}
            onClose={() => setStagePickerProject(null)}
            onSaved={loadBoard}
            project={stagePickerProject}
          />
        )}
        {linkedRecordEditor && (
          <MasterBoardQuickEditor
            features={features}
            canAccessBilling={canAccessBilling}
            canManageRecords={canManageRecords}
            companyContext={data?.company_context}
            initialPanel={linkedRecordEditor.panel}
            onClose={() => setLinkedRecordEditor(null)}
            onSaved={loadBoard}
            projectId={linkedRecordEditor.projectId}
          />
        )}
        {canHideColumns && (
          <BoardColumnsDialog
            columns={colVis.availableColumns}
            dialogTestId="manager-board-columns-dialog"
            hidden={colVis.hidden}
            idPrefix="manager"
            onOpenChange={colVis.setOpen}
            onResetToDefault={colVis.resetToDefault}
            onShowAll={colVis.showAll}
            onToggle={colVis.toggle}
            open={colVis.open}
          />
        )}
        {canManageShared && (
          <Dialog onOpenChange={setAddDividerOpen} open={addDividerOpen}>
            <DialogContent data-testid="manager-board-divider-dialog">
              <DialogHeader><DialogTitle>Add divider</DialogTitle></DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); submitDivider(); }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <label>Label
                  <input autoFocus data-testid="manager-board-divider-label"
                    onChange={(e) => setDividerDraft({ ...dividerDraft, label: e.target.value })}
                    placeholder="e.g. NEXT WEEK" required value={dividerDraft.label} />
                </label>
                <label>Section
                  <select data-testid="manager-board-divider-section"
                    onChange={(e) => setDividerDraft({ ...dividerDraft, section_key: e.target.value })}
                    value={dividerDraft.section_key}>
                    <option value="in_progress">In Progress</option>
                  </select>
                </label>
                <DialogFooter>
                  <button data-testid="manager-board-divider-submit" type="submit">Add divider</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}

        <BoardHighlightPopover
          current={highlightPopover?.current}
          onClose={() => setHighlightPopover(null)}
          onPick={applyHighlight}
          open={!!highlightPopover}
          position={highlightPopover?.position}
        />
        {drag.indicator}

      </BoardWorkspace>
    </div>
  );
}

function ManagerRow({
  canAccessBilling, canCreateFieldNotes, canManageProjectWorkflow, canManageRecords,
  canOrganize, canQuickEdit, gridCellProps, gripProps, hiddenColumns,
  onEdit, onHighlightRequest, onLinkedEdit, onMoveClick, onQuickEdit, onStagePick,
  row, rowProps,
}) {
  const highlights = row.cell_highlights || {};
  const navigate = useNavigate();
  const sectionKey = row.operational_status || "in_progress";
  const isHidden = (column) => hiddenColumns?.has(column);
  const cellProps = (column) => {
    const bgColor = highlights[column];
    const style = bgColor ? {
      background: `color-mix(in srgb, var(--bo-${bgColor}, ${bgColor}) 18%, transparent)`,
      color: "#111",
    } : undefined;
    return {
      ...(gridCellProps ? gridCellProps({ rowKey: row.id, colKey: column }) : {}),
      "data-testid": `manager-cell-${column}-${row.id}`,
      "data-highlight": bgColor || undefined,
      onContextMenu: (event) => onHighlightRequest(event, row.id, column, bgColor, sectionKey),
      style,
    };
  };
  return (
    <tr
      data-testid={`manager-board-row-${row.id}`}
      data-board-row-id={row.id}
      data-board-row-kind="project"
      data-board-section-key={sectionKey}
      data-bo-version={row._bo_version ?? 0}
      onDoubleClick={() => canQuickEdit && onQuickEdit(row)}
      {...(rowProps || {})}
    >
      {canOrganize && (
        <td className="board-drag-cell" data-testid={`manager-drag-${row.id}`}>
          <button
            aria-label="Drag to reorder"
            className="board-drag-handle"
            data-testid={`manager-move-${row.id}`}
            onClick={(event) => { event.stopPropagation(); }}
            type="button"
            disabled={!gripProps}
            {...(gripProps || {})}
          >
            <GripVertical size={14} />
          </button>

        </td>
      )}
      <td className="table-project-column" {...cellProps("project_number")}>
        <Link data-testid={`manager-project-link-${row.id}`} state={MANAGER_PROJECT_LINK_STATE} to={`/projects/${row.id}`}>
          <div className="master-project-identity" data-testid={`manager-project-identity-${row.id}`} style={{ minWidth: 0 }}>
            <strong style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
              {row.project_name || row.project_number}
            </strong>
            {row.project_name && row.project_name !== row.project_number && (
              <span style={{ fontSize: 11, color: "#64748b" }}>{row.project_number}</span>
            )}
          </div>
        </Link>
      </td>
      {!isHidden("due_date") && (
        <td data-testid={`manager-cell-due-date-${row.id}`} {...cellProps("due_date")}>{formatBoardDate(row.due_date)}</td>
      )}
      {!isHidden("operational_status") && (
        <td {...cellProps("operational_status")}>
          <span
            className={`stage-pill state-${row.operational_status || "not_started"}`}
            data-testid={`manager-stage-pill-${row.id}`}
            onClick={(e) => { e.stopPropagation(); onStagePick(); }}
            role="button"
            tabIndex={0}
          >
            {operationalStatusLabel(row)}
          </span>
        </td>
      )}
      {!isHidden("customer_supervisor") && (
        <td {...cellProps("customer_supervisor")}>{row.customer_supervisor || "—"}</td>
      )}
      {!isHidden("permit_status") && (
        <td {...cellProps("permit_status")}
            onDoubleClick={(e) => { e.stopPropagation(); if (canManageRecords) onLinkedEdit({ projectId: row.id, panel: "permits" }); }}>
          <PermitReadinessLabel
            reason={row.permit_status_reason}
            state={row.permit_status}
            testId={`manager-permit-${row.id}`}
          />
        </td>
      )}
      {!isHidden("locate_status") && (
        <td {...cellProps("locate_status")}
            onDoubleClick={(e) => { e.stopPropagation(); if (canManageRecords) onLinkedEdit({ projectId: row.id, panel: "locates" }); }}>
          {(() => {
            const view = locateCellView(row);
            return (
              <span
                className="informational-status-text"
                data-testid={`manager-locate-${row.id}`}
                title={view.label}
                style={{
                  color: view.variant.color,
                  backgroundColor: view.variant.background,
                  padding: view.variant.background === "transparent" ? 0 : "2px 6px",
                  borderRadius: 4,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {view.label}
              </span>
            );
          })()}
        </td>
      )}
      {!isHidden("locate_ticket_number") && (
        <td {...cellProps("locate_ticket_number")}
            data-testid={`manager-locate-ticket-${row.id}`}
            onDoubleClick={(e) => { e.stopPropagation(); if (canManageRecords) onLinkedEdit({ projectId: row.id, panel: "locates" }); }}
            title={row.locate_notes || ""}>
          {row.locate_ticket_number || "—"}
        </td>
      )}
      {!isHidden("water_locate") && (
        <td {...cellProps("water_locate")}><ManagerWaterLocateCell row={row} /></td>
      )}
      {!isHidden("next_scheduled_date") && (
        <td data-testid={`manager-cell-scheduled-date-${row.id}`} {...cellProps("next_scheduled_date")}>{formatBoardDate(row.next_scheduled_date)}</td>
      )}
      {!isHidden("recent_notes") && (
        <td {...cellProps("recent_notes")} style={{ width: 300, maxWidth: 300 }}>
          <BoardNotesPreview
            onOpen={() => navigate(`/projects/${row.id}#general-project-notes`)}
            row={row}
            testId={`manager-recent-notes-${row.id}`}
          />
        </td>
      )}
      <td>
        <ManagerRowAction canCreateFieldNotes={canCreateFieldNotes} canManageProjectWorkflow={canManageProjectWorkflow} onEdit={onEdit} row={row} />
      </td>
    </tr>
  );
}

function ManagerRowAction({ canCreateFieldNotes, canManageProjectWorkflow, onEdit, row }) {
  if (canManageProjectWorkflow) {
    return (
      <button className="table-edit-button" data-testid={`manager-edit-${row.id}`} onClick={() => onEdit(row)} type="button">
        <Pencil size={12} />
      </button>
    );
  }
  if (canCreateFieldNotes) {
    return (
      <Link className="table-open-link" data-testid={`manager-add-note-${row.id}`}
            state={MANAGER_PROJECT_LINK_STATE} to={`/projects/${row.id}`}>
        <ExternalLink size={12} />
      </Link>
    );
  }
  return <span data-testid={`manager-read-only-${row.id}`}>—</span>;
}

function ManagerWaterLocateCell({ row }) {
  const water = row.water_readiness || {};
  if (!row.water_locate_required) return <span data-testid={`manager-water-locate-not-required-${row.id}`}>—</span>;
  if (!row.water_locate_submitted_at) return <InformationalStatusText label="pending" testId={`manager-water-locate-submission-required-${row.id}`} />;
  if (water.state === "invalid") return <InformationalStatusText label={water.reason || "invalid"} testId={`manager-water-locate-invalid-${row.id}`} />;
  return <InformationalStatusText label={water.reason || "ready"} testId={`manager-water-locate-ready-${row.id}`} />;
}


